import { useState, useMemo } from "react";
import { Tag, X, Check, Minus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useUserTags, getTagColorClass } from "@/hooks/useUserTags";
import { cn } from "@/lib/utils";

interface BulkActionsToolbarProps {
  selectedCount: number;
  totalFilteredCount: number;
  onSelectAll: () => void;
  onClearSelection: () => void;
  selectedUserIds: string[];
}

type TagState = 'all' | 'some' | 'none';

export const BulkActionsToolbar = ({
  selectedCount,
  totalFilteredCount,
  onSelectAll,
  onClearSelection,
  selectedUserIds,
}: BulkActionsToolbarProps) => {
  const { allTags, assignmentsByUser, bulkManageUserTags, isBulkManaging } = useUserTags();
  const [showTagsPopover, setShowTagsPopover] = useState(false);
  const queryClient = useQueryClient();

  const memberTypeMutation = useMutation({
    mutationFn: async (member_type: string) => {
      const { data, error } = await supabase.functions.invoke("admin-user-actions", {
        body: { action: "bulk_update_member_type", data: { user_ids: selectedUserIds, member_type } },
      });
      if (error) {
        const body = await (error as { context?: Response }).context?.json?.().catch(() => null);
        throw new Error(body?.error || error.message);
      }
      if (data?.error) throw new Error(data.error);
      return data;
    },
    onSuccess: (_d, member_type) => {
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
      queryClient.invalidateQueries({ queryKey: ["host-candidates"] });
      toast.success(`Updated ${selectedUserIds.length} users to ${member_type}`);
      onClearSelection();
    },
    onError: (e: Error) => toast.error(`Failed to update member type: ${e.message}`),
  });

  // Only manual tags can be assigned/removed by users
  const manualTags = allTags.filter((tag) => tag.tag_type === 'manual');

  // Calculate the state of each tag for selected users
  const tagStates = useMemo(() => {
    const states: Record<string, TagState> = {};
    
    for (const tag of manualTags) {
      let assignedCount = 0;
      for (const userId of selectedUserIds) {
        const userAssignments = assignmentsByUser[userId] || [];
        if (userAssignments.some((a) => a.tag_id === tag.id)) {
          assignedCount++;
        }
      }
      
      if (assignedCount === 0) {
        states[tag.id] = 'none';
      } else if (assignedCount === selectedUserIds.length) {
        states[tag.id] = 'all';
      } else {
        states[tag.id] = 'some';
      }
    }
    
    return states;
  }, [manualTags, selectedUserIds, assignmentsByUser]);

  // Track pending changes locally
  const [pendingChanges, setPendingChanges] = useState<Record<string, 'add' | 'remove' | null>>({});

  const getEffectiveState = (tagId: string): TagState => {
    const pending = pendingChanges[tagId];
    if (pending === 'add') return 'all';
    if (pending === 'remove') return 'none';
    return tagStates[tagId] || 'none';
  };

  const handleTagClick = (tagId: string) => {
    const currentState = tagStates[tagId];
    const pendingState = pendingChanges[tagId];

    setPendingChanges((prev) => {
      const newChanges = { ...prev };
      
      // Cycle through states: none -> all -> none (or some -> all -> none -> some...)
      if (pendingState === 'add') {
        // Currently set to add, clicking removes
        newChanges[tagId] = 'remove';
      } else if (pendingState === 'remove') {
        // Currently set to remove, clicking resets to original
        delete newChanges[tagId];
      } else {
        // No pending change
        if (currentState === 'none') {
          newChanges[tagId] = 'add';
        } else if (currentState === 'all') {
          newChanges[tagId] = 'remove';
        } else {
          // some - clicking adds to all
          newChanges[tagId] = 'add';
        }
      }
      
      return newChanges;
    });
  };

  const handleApplyChanges = async () => {
    const tagsToAdd: string[] = [];
    const tagsToRemove: string[] = [];

    for (const [tagId, action] of Object.entries(pendingChanges)) {
      if (action === 'add') tagsToAdd.push(tagId);
      if (action === 'remove') tagsToRemove.push(tagId);
    }

    if (tagsToAdd.length === 0 && tagsToRemove.length === 0) return;

    await bulkManageUserTags({
      userIds: selectedUserIds,
      tagsToAdd,
      tagsToRemove,
    });
    
    setShowTagsPopover(false);
    setPendingChanges({});
    onClearSelection();
  };

  const hasChanges = Object.keys(pendingChanges).length > 0;

  const handleOpenChange = (open: boolean) => {
    setShowTagsPopover(open);
    if (!open) {
      setPendingChanges({});
    }
  };

  return (
    <div className="flex items-center gap-3 p-3 bg-primary/5 rounded-lg border border-primary/20 mb-4">
      <span className="text-sm font-medium">
        {selectedCount} of {totalFilteredCount} users selected
      </span>

      <Button variant="link" size="sm" className="h-auto p-0" onClick={onSelectAll}>
        Select all {totalFilteredCount}
      </Button>

      <Button variant="link" size="sm" className="h-auto p-0" onClick={onClearSelection}>
        Clear selection
      </Button>

      <Separator orientation="vertical" className="h-5" />

      <Select
        value=""
        onValueChange={(v) => memberTypeMutation.mutate(v)}
        disabled={memberTypeMutation.isPending}
      >
        <SelectTrigger className="h-9 w-[170px]">
          <SelectValue placeholder={memberTypeMutation.isPending ? "Updating..." : "Set member type"} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="member">Member</SelectItem>
          <SelectItem value="host">Host</SelectItem>
          <SelectItem value="artist">Artist</SelectItem>
        </SelectContent>
      </Select>

      {/* Manage Tags Popover */}
      <Popover open={showTagsPopover} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <Button variant="secondary" size="sm">
            <Tag className="h-4 w-4 mr-2" />
            Manage Tags
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-72 p-3" align="start">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-medium">Manage tags for {selectedCount} users</p>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={() => handleOpenChange(false)}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mb-3">
            Click to toggle: <Check className="inline h-3 w-3" /> all have, <Minus className="inline h-3 w-3" /> some have, empty = none have
          </p>
          <div className="space-y-1 max-h-64 overflow-y-auto">
            {manualTags.map((tag) => {
              const effectiveState = getEffectiveState(tag.id);
              const originalState = tagStates[tag.id];
              const hasPendingChange = pendingChanges[tag.id] !== undefined;
              
              return (
                <button
                  key={tag.id}
                  type="button"
                  onClick={() => handleTagClick(tag.id)}
                  className={cn(
                    "w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-left transition-colors",
                    "hover:bg-muted/50",
                    hasPendingChange && "bg-muted/30"
                  )}
                >
                  <div className={cn(
                    "w-5 h-5 border rounded flex items-center justify-center shrink-0",
                    effectiveState === 'all' && "bg-primary border-primary text-primary-foreground",
                    effectiveState === 'some' && "bg-muted border-muted-foreground/50",
                    effectiveState === 'none' && "border-input"
                  )}>
                    {effectiveState === 'all' && <Check className="h-3.5 w-3.5" />}
                    {effectiveState === 'some' && <Minus className="h-3.5 w-3.5 text-muted-foreground" />}
                  </div>
                  <Badge
                    variant="outline"
                    className={cn("text-xs", getTagColorClass(tag.color))}
                  >
                    {tag.name}
                  </Badge>
                  {hasPendingChange && (
                    <span className="text-xs text-muted-foreground ml-auto">
                      {pendingChanges[tag.id] === 'add' ? '(will add)' : '(will remove)'}
                    </span>
                  )}
                </button>
              );
            })}
            {manualTags.length === 0 && (
              <p className="text-xs text-muted-foreground py-2">No manual tags available</p>
            )}
          </div>
          <Button
            className="w-full mt-3"
            size="sm"
            onClick={handleApplyChanges}
            disabled={!hasChanges || isBulkManaging}
          >
            {isBulkManaging ? "Applying..." : "Apply Changes"}
          </Button>
        </PopoverContent>
      </Popover>
    </div>
  );
};
